import '@knap-ai/widget/style.css'

import type {
  IKnapWidget,
  IKnapWidgetInitParams,
  IKnapWidgetUpdateParams,
} from './types'

// Map Hanwha design tokens (hex) → widget shadcn tokens (HSL `H S% L%`).
const HANWHA_WIDGET_CSS_VARS: Record<string, string> = {
  // Panel body / sidebar surfaces — chatbot/chatbot-bg
  '--background': '40 21% 88.6%', // #e8e4dc
  '--foreground': '45 10% 15.7%', // #2c2a24 content/primary
  '--card': '40 21% 88.6%',
  '--card-foreground': '45 10% 15.7%',
  '--popover': '40 21% 88.6%',
  '--popover-foreground': '45 10% 15.7%',
  // Brand — user message bubble, send button, focus ring
  '--primary': '235 32% 30.6%', // #353967 brand/accent
  '--primary-foreground': '40 21% 88.6%',
  // Secondary / muted surfaces — input box, task progress card
  '--secondary': '38 16% 81.6%', // #d8d3c8 chatbot/chatbot-input
  '--secondary-foreground': '45 10% 15.7%',
  '--muted': '38 16% 81.6%',
  '--muted-foreground': '34 4% 47.8%', // #807c76 content/secondary
  '--accent': '38 16% 81.6%',
  '--accent-foreground': '45 10% 15.7%',
  // Dividers — chatbot/chatbot-dividers
  '--border': '45 10.5% 77.6%', // #ccc9c0
  '--input': '38 16% 81.6%',
  '--ring': '235 32% 30.6%',
  // Sidebar (used in widget shell)
  '--sidebar': '40 21% 88.6%',
  '--sidebar-foreground': '45 10% 15.7%',
  '--sidebar-border': '45 10.5% 77.6%',
  '--sidebar-accent': '38 16% 81.6%',
  '--sidebar-accent-foreground': '45 10% 15.7%',
  // Shape
  '--radius': '0.75rem',
  // Suggestion bubble — Tiempos Light, sharp edges, wider padding
  '--knap-suggestion-font-family': '"Test Tiempos Headline", serif',
  '--knap-suggestion-font-weight': '300',
  '--knap-suggestion-padding-x': '20px',
  '--knap-suggestion-padding-y': '12px',
  '--knap-suggestion-radius': '0',
  // Chat input pill — Figma node 2742-12474
  '--knap-chat-input-bg': '#d8d3c8', // chatbot/chatbot-input
  '--knap-chat-input-border-color': '#ccc9c0', // chatbot/chatbot-dividers
  '--knap-chat-input-radius': '1.5rem',
  '--knap-chat-input-placeholder-color': '#807c76', // content/secondary
  '--knap-chat-send-button-bg': '#353967', // brand/accent
  '--knap-chat-send-button-color': '#e8e4dc',
  '--knap-chat-send-button-radius': '9999px',
}

const HANWHA_WIDGET_FONT_FAMILY =
  '"Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'

export const initKnapWidget = async (
  params: IKnapWidgetInitParams,
): Promise<IKnapWidget> => {
  const mod = await import('@knap-ai/widget')
  const KnapsackWidget = mod.default
  // Fail visibly rather than silently accepting token updates on an older SDK.
  if (
    !(KnapsackWidget as unknown as { supportsSafeLifecycle?: boolean })
      .supportsSafeLifecycle
  ) {
    throw new Error('Widget lifecycle support is unavailable')
  }
  const config = {
    apiUrl: import.meta.env.VITE_API_URL,
    initializationSignal: params.initializationSignal,
    container: params.container,
    mode: 'chat' as const,
    width: '100%',
    height: '100%',
    theme: params.theme,
    accessToken: params.accessToken,
    user: {
      email: params.user.email,
      name: params.user.name ?? '',
    },
    cssVars: HANWHA_WIDGET_CSS_VARS,
    fontFamily: HANWHA_WIDGET_FONT_FAMILY,
    showKnapsButton: false,
    showConnectionsButton: false,
    disableUserConnectors: true,
    hideConnectionSuggestions: true,
    defaultSidebarOpen: false,
  }
  const instance = await KnapsackWidget.init(config)
  return {
    destroy: (): void => instance.destroy(),
    update: async (next: IKnapWidgetUpdateParams): Promise<void> => {
      await instance.update(next)
    },
  }
}
